import { cn } from '@forest/shared'
import {
  createEffect,
  createSignal,
  For,
  Match,
  on,
  onCleanup,
  Show,
  Switch,
  type Accessor,
} from 'solid-js'
import type { ShopTab } from '~/lib/deep-link'
import { FIRST_REVEAL_COUNT } from '~/store/catalogue-grid'
import { BalancePillsSkeleton } from './balance-pills'
import { CatalogueGridPlaceholder } from './catalogue-card'
import { CurrencyCataloguePlaceholder } from './currency-catalogue'
import { FeaturedCataloguePlaceholder } from './featured-catalogue'
import { PLACEHOLDER_BAR_CLASS } from './placeholder-bar'
import {
  CATALOGUE_COLUMN_CLASS,
  CATALOGUE_TOP,
  CATALOGUE_TOP_UNDER_CLASSIC_CONTROLS,
  CLASSIC_CHIP_SIZE_CLASS,
  CLASSIC_CONTROLS_ROW_CLASS,
  CLASSIC_SORT_SIZE_CLASS,
  CloseShopButton,
  HEADER_ROW_CLASS,
  ShopHeader,
  TAB_ROW_CLASS,
  TAB_SIZE_CLASS,
} from './shop-header'

// The reveal's crossfade, after transitions.dev's "Skeleton loader and
// reveal": the skeleton fades and blurs out while the real Shop beneath
// fades and sharpens in, both over 400ms ease-in-out; then the skeleton
// unmounts. Keep BOOT_SKELETON_FADE_MS in step with the class's duration.
// Arbitrary properties, not `duration-[…]` / `ease-in-out`: tailwindcss-animate
// makes the former ambiguous (Tailwind then skips it), and Tailwind's
// `ease-in-out` is its own curve, not CSS's.
export const BOOT_SKELETON_FADE_MS = 400
export const REVEAL_TRANSITION_CLASS =
  'transition-[opacity,filter] [transition-duration:400ms] [transition-timing-function:ease-in-out] motion-reduce:transition-none'
// The side of the crossfade not showing: the skeleton after the reveal,
// the real Shop before it.
export const REVEAL_HIDDEN_CLASS = 'opacity-0 blur-[2px]'

const TAB_COUNT = 3
const CLASSIC_CHIP_COUNT = 2

// The Classic content filter and sort row's placeholder, shared by the boot
// skeleton and the Classic reveal's catalogue skeleton.
export function ClassicControlsPlaceholder() {
  return (
    <div
      aria-hidden="true"
      class={cn(CLASSIC_CONTROLS_ROW_CLASS, 'animate-pulse')}
    >
      <div class="flex gap-2">
        <For each={Array.from({ length: CLASSIC_CHIP_COUNT })}>
          {() => <div class={cn(CLASSIC_CHIP_SIZE_CLASS, 'w-24 bg-ink/10')} />}
        </For>
      </div>
      <div
        class={cn(
          CLASSIC_SORT_SIZE_CLASS,
          'ml-auto flex items-center justify-center',
        )}
      >
        <div class={cn(PLACEHOLDER_BAR_CLASS, 'size-5')} />
      </div>
    </div>
  )
}

/**
 * The boot skeleton (GLOSSARY.md; ADR 0048): Shop's only boot loading UI,
 * painted from the first frame over the real Shop, which mounts beneath it
 * as soon as its data is ready. A fixed placeholder of the landing tab —
 * its shape from the launch parameters, never from data — with a working
 * close button and inert bars for the balance pills and tabs. Every block
 * takes the real element's footprint from the same shared classes, so the
 * reveal's crossfade shifts nothing. At the Shop reveal it fades out, lets
 * touches through to the real Shop at once, and unmounts.
 *
 * It sits above the deep-link detail dialog (z-30/31), which presents
 * beneath it, and below the shared dialog layer (z-70), so the bootstrap
 * error dialog opens over it.
 */
export function BootSkeleton(props: {
  revealed: Accessor<boolean>
  shape: ShopTab
}) {
  const [gone, setGone] = createSignal(false)
  createEffect(
    on(
      () => props.revealed(),
      (revealed) => {
        if (!revealed) return
        const fade = setTimeout(() => setGone(true), BOOT_SKELETON_FADE_MS)
        onCleanup(() => clearTimeout(fade))
      },
    ),
  )

  return (
    <Show when={!gone()}>
      <div
        data-testid="boot-skeleton"
        data-shape={props.shape}
        data-state={props.revealed() ? 'leaving' : 'showing'}
        aria-busy="true"
        // pointer-events-auto: a modal dialog open beneath (the deep-link
        // detail) turns pointer events off on the body; the close button
        // must still take taps.
        class={cn(
          'fixed inset-0 z-[60] min-w-[320px] overflow-hidden bg-page',
          REVEAL_TRANSITION_CLASS,
          props.revealed()
            ? cn('pointer-events-none', REVEAL_HIDDEN_CLASS)
            : 'pointer-events-auto',
        )}
      >
        <ShopHeader>
          <div class={HEADER_ROW_CLASS}>
            <CloseShopButton />
            <BalancePillsSkeleton testId="boot-skeleton-balances" />
          </div>
          <div
            data-testid="boot-skeleton-tabs"
            aria-hidden="true"
            class={cn(TAB_ROW_CLASS, 'animate-pulse')}
          >
            <For each={Array.from({ length: TAB_COUNT })}>
              {() => (
                <div
                  class={cn(TAB_SIZE_CLASS, 'flex items-center justify-center')}
                >
                  <div
                    class={cn(PLACEHOLDER_BAR_CLASS, 'h-3 w-20 bg-ink/15')}
                  />
                </div>
              )}
            </For>
          </div>
          <Show when={props.shape === 'classic'}>
            <ClassicControlsPlaceholder />
          </Show>
        </ShopHeader>

        <div
          aria-hidden="true"
          class={cn(
            'animate-pulse',
            CATALOGUE_COLUMN_CLASS,
            props.shape === 'classic'
              ? CATALOGUE_TOP_UNDER_CLASSIC_CONTROLS
              : CATALOGUE_TOP,
          )}
        >
          <Switch>
            <Match when={props.shape === 'featured'}>
              <FeaturedCataloguePlaceholder />
            </Match>
            <Match when={props.shape === 'classic'}>
              <CatalogueGridPlaceholder count={FIRST_REVEAL_COUNT} />
            </Match>
            <Match when={props.shape === 'currency'}>
              <CurrencyCataloguePlaceholder />
            </Match>
          </Switch>
        </div>
      </div>
    </Show>
  )
}
